import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { Cross2Icon } from "@radix-ui/react-icons";
import usePortfolioIcon from "../../../hooks/usePortfolioIcon";
import useRecommendedPortfolio from "../../../hooks/useRecommendedPortfolio";
import PortfolioDialog from "../PortfolioDialog";
import { useAnswer } from "../../../util/form";
import { useAppStore } from "../../../store/store";

export default function OffensiveGoalsBased({
  advanceScreen,
  previousScreen,
  validation,
  updateValidation,
  value,
  screen,
}) {
  const { user, formOptions } = useAppStore();
  const setSelectedPortfolioId = useAppStore(
    (state) => state.setSelectedPortfolioId,
  );
  const { answer, setAnswer } = useAnswer(screen.id);
  const [portfolio, setPortfolio] = useState("");
  const [tempPortfolio, setTempPortfolio] = useState("");
  const [selectedOption, setSelectedOption] = useState(""); // Track which radio is selected

  // Get recommended portfolio info for GB path. Previously cached the id
  // in localStorage from screen 2.9; now we fetch from the same source 2.9
  // does so we don't carry stale data across sessions.
  const { recommendedPortfolioInfo } = useRecommendedPortfolio({});
  const recommendedPortfolioId = Number(recommendedPortfolioInfo?.id) || 0;

  // Function to get portfolio name by ID
  function getPortfolioName(portfolioId) {
    if (!portfolioId) return "";
    const matchedPortfolio = formOptions.portfolios.find(p => p.id === portfolioId || p.code === portfolioId);

    return matchedPortfolio ? matchedPortfolio.name : portfolioId;
  }

  // Function to get portfolio description by ID
  function getPortfolioDescription(portfolioId) {
    if (!portfolioId) return "";
    const matchedPortfolio = formOptions.portfolios.find(p => p.id === portfolioId || p.code === portfolioId);

    return matchedPortfolio ? matchedPortfolio.description : portfolioId;
  }

  // Initialize selectedOption from saved answer when component mounts
  useEffect(() => {
    if (answer) {
      // Restore the radio selection based on saved answer
      if (!isNaN(Number(answer))) {
        // If answer is a portfolio ID number
        const portfolioId = Number(answer);
        if (portfolioId === recommendedPortfolioId) {
          setSelectedOption("proceed");
          setPortfolio(portfolioId);
        } else {
          setSelectedOption("choose");
          setPortfolio(answer);
        }
        updateValidation(true);
      } else if (answer === "proceed" || answer === "choose") {
        setSelectedOption(answer);
        updateValidation(answer === "proceed" || portfolio !== "");
      }
    } else if (value.length > 0) {
      updateValidation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize tempPortfolio when modal opens
  useEffect(() => {
    if (selectedOption === "choose" && tempPortfolio === "") {
      setTempPortfolio(portfolio);
    }
  }, [selectedOption, portfolio, tempPortfolio]);

  function updateAnswer(value) {
    setSelectedOption(value); // Track which radio button is selected

    if (value === "proceed") {
      const recommendedId = recommendedPortfolioId || user?.artesysPortfolioCode || "";
      setPortfolio(recommendedId);
      setTempPortfolio("");
      setAnswer(recommendedId);
      setSelectedPortfolioId(recommendedId);
    } else if (value === "choose") {
      if (tempPortfolio) {
        setAnswer(tempPortfolio);
        setSelectedPortfolioId(tempPortfolio);
      } else {
        setAnswer("choose");
      }
    } else {
      setAnswer(value);
      if (!isNaN(Number(value)) && Number(value) > 0) {
        setSelectedPortfolioId(value);
      }
    }
    updateValidation(value !== "");
  }

  function setSelectedPortfolio(id) {
    console.log(`setting tempPortfolio to ${id}`);
    setTempPortfolio(id);
    console.log("Temporary portfolio selection:", id);
  }

  function savePortfolioSelection() {
    console.log(`Saving portfolio selection: ${tempPortfolio}`);
    setPortfolio(tempPortfolio);
    updateAnswer(tempPortfolio);
    console.log("Portfolio saved to form state:", tempPortfolio);
  }

  function cancelPortfolioSelection() {
    console.log("Canceling portfolio selection");
    setTempPortfolio(portfolio); // Reset to current selection
  }

  const isSaveDisabled = tempPortfolio === ""; // Check if tempPortfolio is empty

  const portfolioIcon = usePortfolioIcon(recommendedPortfolioId - 1);

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 text-center md:py-20 md:px-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl mb-9 md:mb-11 md:text-4xl">
            You have been recommended the portfolio:
          </h1>
        </div>
        <div className="mb-4">
          <span className="text-base font-bold text-gray-800 md:text-lg">
            {getPortfolioName(recommendedPortfolioId)}
          </span>
          <Popover.Root>
            <Popover.Trigger asChild>
              <button className="relative ml-[.25em] shadow-none before:absolute before:left-1/2 before:top-1/2 before:block before:h-11 before:w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']">
                <FontAwesomeIcon
                  icon="fa-regular fa-circle-info"
                  className="text-[.944em] text-as-green-100"
                />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                sideOffset={5}
                className="z-50 w-64 rounded bg-white p-5 shadow-xl will-change-[transform,opacity] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-as-green-50 data-[side=bottom]:animate-slide-up-fade data-[side=left]:animate-slide-right-fade data-[side=right]:animate-slide-left-fade data-[side=top]:animate-slide-down-fade"
              >
                <div className="text-xs">
                  <p>{getPortfolioDescription(recommendedPortfolioId)}</p>
                </div>
                <Popover.Close
                  className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full font-[inherit] text-gray-500 shadow-none hover:bg-gray-100 hover:text-gray-900"
                  aria-label="Close"
                >
                  <Cross2Icon />
                </Popover.Close>
                <Popover.Arrow className="fill-white" />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
        <img
          className="block w-64 h-auto mx-auto mb-3 md:mb-4 md:w-72"
          src={portfolioIcon}
          alt=""
        />
        <div className="flex items-center justify-center mb-10 gap-x-5 md:mb-14">
          <div className="flex items-center gap-x-2">
            <div className="w-6 h-3 bg-as-green-50"></div>
            <div className="text-sm font-bold text-black">Fixed Income</div>
          </div>
          <div className="flex items-center gap-x-2">
            <div className="w-6 h-3 bg-as-green-100"></div>
            <div className="text-sm font-bold text-black">Equities</div>
          </div>
        </div>
        <form>
          <div className="max-w-[37.125rem] mx-auto">
            <h2 className="mb-6 text-xl font-light md:text-2xl md:mb-8">
              How would you like to proceed?
            </h2>
            <RadioGroup.Root
              className="space-y-3"
              aria-label="How would you like to proceed?"
              value={selectedOption}
            >
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="proceed"
                  id="proceed"
                  onClick={() => updateAnswer("proceed")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="proceed"
                  >
                    I am happy with this portfolio.
                  </label>
                </RadioGroup.Item>
              </div>
              {answer === "proceed" && (
                <div className="px-6 py-6 text-left border border-gray-300 rounded-md md:mt-12 md:px-12 md:py-8">
                  <p className="text-base font-bold text-gray-800 md:text-lg">
                    You’re ready to put Artesys to work.
                  </p>
                  <p className="mt-4 text-gray-700 md:mt-6">
                    Here’s how we will optimize your new portfolio.
                  </p>
                  <ol className="mt-3 text-gray-700 list md:mt-5">
                    <li>
                      Strategic Allocation Selection
                      <ol>
                        <li>
                          Our team analyzes the best asset classes and funds
                          available in an attempt to achieve the best results.
                        </li>
                      </ol>
                    </li>
                    <li>
                      Active Management
                      <ol>
                        <li>Your portfolio will be rebalanced quarterly.</li>
                        <li>
                          Artesys will also make active allocation adjustments
                          to your portfolio in response to the market
                          environment.
                        </li>
                      </ol>
                    </li>
                    <li>
                      Tactical Adjustments
                      <ol>
                        <li>Use language from CIT deck.</li>
                      </ol>
                    </li>
                  </ol>
                </div>
              )}
              <div className="flex items-center">
                <RadioGroup.Item
                  className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                  value="choose"
                  id="choose"
                  onClick={() => updateAnswer("choose")}
                >
                  <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                  <label
                    className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                    htmlFor="choose"
                  >
                    I would like to select my own portfolio.
                  </label>
                  {selectedOption === "choose" && (
                    <Dialog.Root>
                      {portfolio === "" ? (
                        <Dialog.Trigger asChild>
                          <div
                            className="inline-flex mt-4 cursor-pointer btn btn--small btn--secondary"
                            role="button"
                            tabIndex={0}
                          >
                            Select Portfolio
                            <FontAwesomeIcon
                              icon="fa-solid fa-plus"
                              className="ml-2.5"
                            />
                          </div>
                        </Dialog.Trigger>
                      ) : (
                        <Dialog.Trigger asChild>
                          <div
                            className="btn btn--secondary mt-4 inline-flex !w-full flex-wrap justify-between gap-2 !py-4 !text-sm cursor-pointer"
                            role="button"
                            tabIndex={0}
                          >
                            <span className="text-left text-gray-800">
                              {getPortfolioName(portfolio)}
                            </span>
                            <span className="font-medium text-right text-teal-800 underline hover:text-as-green-100">
                              Change
                            </span>
                          </div>
                        </Dialog.Trigger>
                      )}

                      <Dialog.Portal>
                        <Dialog.Overlay className="fixed inset-0 z-[100] animate-fade-in overflow-y-auto overflow-x-hidden bg-teal-900/80">
                          <div className="max-w-full wrapper">
                            <Dialog.Content
                              className="w-full max-w-6xl mx-auto my-12 bg-white rounded-md shadow-xl"
                              onInteractOutside={(event) =>
                                event.preventDefault()
                              }
                            >
                              <div className="p-6">
                                <Dialog.Title className="text-2xl text-gray-800">
                                  Select Portfolio
                                </Dialog.Title>
                                <Dialog.Description className="mt-2">
                                  Choose which portfolio makes sense for you.
                                </Dialog.Description>
                              </div>
                              <div className="px-6 pb-6">
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                  <PortfolioDialog
                                    portfolio={tempPortfolio}
                                    setPortfolio={setSelectedPortfolio}
                                    recommendedPortfolioName={getPortfolioName(
                                      user.artesysPortfolioCode,
                                    )}
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end p-6 bg-gray-50">
                                <div className="flex gap-3">
                                  <Dialog.Close asChild>
                                    <button
                                      className="btn btn--secondary"
                                      onClick={cancelPortfolioSelection}
                                    >
                                      Cancel
                                    </button>
                                  </Dialog.Close>
                                  <Dialog.Close asChild>
                                    <button
                                      className={`btn btn--primary ${
                                        isSaveDisabled
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }`}
                                      disabled={isSaveDisabled}
                                      onClick={savePortfolioSelection}
                                    >
                                      Save
                                    </button>
                                  </Dialog.Close>
                                </div>
                              </div>
                            </Dialog.Content>
                          </div>
                        </Dialog.Overlay>
                      </Dialog.Portal>
                    </Dialog.Root>
                  )}
                </RadioGroup.Item>
              </div>
            </RadioGroup.Root>

            <div className="flex justify-center gap-3 mt-6 md:mt-10 md:gap-5">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={previousScreen}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={Boolean(validation) === false}
                onClick={advanceScreen}
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
